import { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { getUpcomingDrops, getMySessions, optIn, cancelSession, generateInviteCodes, DropInfo, Session } from '../api';

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const [drops, setDrops] = useState<DropInfo[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [optingInDate, setOptingInDate] = useState<string | null>(null);
  const [cancellingToken, setCancellingToken] = useState<string | null>(null);
  const [inviteCodes, setInviteCodes] = useState<string[]>([]);
  const [generatingCodes, setGeneratingCodes] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    const [dropsRes, sessionsRes] = await Promise.all([
      getUpcomingDrops(),
      getMySessions(),
    ]);
    if (dropsRes.data) setDrops(dropsRes.data.drops);
    if (sessionsRes.data) setSessions(sessionsRes.data.sessions);
    setLoading(false);
  }

  async function handleOptIn(dropDate: string) {
    setOptingInDate(dropDate);
    setError('');
    const { error: err } = await optIn(dropDate);
    setOptingInDate(null);

    if (err) {
      setError(err);
      return;
    }

    await loadData();
  }

  async function handleCancel(token: string) {
    setCancellingToken(token);
    setError('');
    const { error: err } = await cancelSession(token);
    setCancellingToken(null);

    if (err) {
      setError(err);
      return;
    }

    await loadData();
  }

  async function handleGenerateCodes() {
    setGeneratingCodes(true);
    const { data, error: err } = await generateInviteCodes(3);
    setGeneratingCodes(false);

    if (err) {
      setError(err);
      return;
    }

    if (data) {
      setInviteCodes(data.codes);
    }
  }

  const profileComplete = user?.phone && user?.pizzaPreference;

  return (
    <div className="page">
      <div style={{ maxWidth: 600, margin: '40px auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1>AK Pizza Bot</h1>
          <div>
            <span style={{ marginRight: 12, color: '#666' }}>{user?.displayName}</span>
            <a href="/settings" style={{ marginRight: 12 }}>Settings</a>
            <button onClick={logout} className="btn-secondary">Logout</button>
          </div>
        </div>

        {!profileComplete && (
          <div className="card" style={{ background: '#fff3cd', borderLeft: '4px solid #ffc107', marginBottom: 16 }}>
            <strong>Complete your profile</strong>
            <p>Add your phone number and pizza preference in <a href="/settings">Settings</a> before the next drop.</p>
          </div>
        )}

        <div className="card">
          <h2>Upcoming Drops</h2>
          {loading ? (
            <p>Loading...</p>
          ) : drops.length > 0 ? (
            <div>
              {drops.map((drop) => (
                <div
                  key={drop.dropDate}
                  style={{
                    padding: '12px 0',
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <strong>{drop.dayOfWeek}</strong> — {drop.dropDate}
                    <div style={{ color: '#666', fontSize: 13 }}>12:00 PM PT</div>
                  </div>
                  <div>
                    {drop.isOptedIn ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: '#27ae60', fontWeight: 'bold', fontSize: 13 }}>Opted In</span>
                        {drop.sessionToken && (
                          <a href={`/drop/${drop.sessionToken}`} className="btn-secondary" style={{ fontSize: 13, padding: '4px 10px', textDecoration: 'none' }}>
                            View
                          </a>
                        )}
                      </div>
                    ) : !profileComplete ? (
                      <span style={{ color: '#999', fontSize: 13 }}>Complete profile first</span>
                    ) : (
                      <button
                        onClick={() => handleOptIn(drop.dropDate)}
                        className="btn-primary"
                        disabled={optingInDate === drop.dropDate}
                        style={{ fontSize: 13, padding: '6px 14px' }}
                      >
                        {optingInDate === drop.dropDate ? 'Opting in...' : 'Opt In'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>No upcoming drops scheduled.</p>
          )}
        </div>

        {error && <div className="error">{error}</div>}

        {!loading && sessions.length > 0 && (
          <div className="card" style={{ marginTop: 16 }}>
            <h2>My Registrations</h2>
            {sessions.map((session) => (
              <div
                key={session.sessionToken}
                style={{
                  padding: '10px 0',
                  borderBottom: '1px solid #eee',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <strong>{session.dropDate}</strong>
                  <span
                    style={{
                      marginLeft: 8,
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 'bold',
                      background:
                        session.status === 'registered' ? '#e8f5e9' :
                        session.status === 'completed' ? '#e3f2fd' :
                        session.status === 'failed' ? '#ffebee' : '#f5f5f5',
                      color:
                        session.status === 'registered' ? '#2e7d32' :
                        session.status === 'completed' ? '#1565c0' :
                        session.status === 'failed' ? '#c62828' : '#666',
                    }}
                  >
                    {session.status}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <a href={`/drop/${session.sessionToken}`} className="btn-secondary" style={{ fontSize: 13, padding: '4px 10px', textDecoration: 'none' }}>
                    View
                  </a>
                  {session.status !== 'confirmed' && (
                    <button
                      onClick={() => handleCancel(session.sessionToken)}
                      className="btn-secondary"
                      disabled={cancellingToken === session.sessionToken}
                      style={{ fontSize: 13, padding: '4px 10px', color: '#c62828' }}
                    >
                      {cancellingToken === session.sessionToken ? 'Removing...' : session.status === 'registered' ? 'Cancel' : 'Dismiss'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {user?.isAdmin && (
          <div className="card" style={{ marginTop: 16 }}>
            <h2>Admin</h2>
            <button
              onClick={handleGenerateCodes}
              className="btn-secondary"
              disabled={generatingCodes}
            >
              {generatingCodes ? 'Generating...' : 'Generate Invite Codes'}
            </button>

            {inviteCodes.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <p>Share these codes with friends:</p>
                <div style={{ fontFamily: 'monospace', background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
                  {inviteCodes.map(code => (
                    <div key={code} style={{ marginBottom: 4 }}>{code}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
